<template>
  <div
    class="grid h-fit shrink-0 grid-cols-[auto_auto] items-center gap-x-4 gap-y-3 *:odd:text-gray-300 [&_p]:even:font-medium [&>p:empty]:even:before:content-['-']"
  >
    <!-- Name -->
    <p>Naam</p>
    <p>{{ meteringPoint?.displayName }}</p>

    <!-- Status -->
    <template v-if="!ASSET_TYPE_HIDDEN_STATUS.includes(mpProps.type)">
      <p>Status</p>
      <div class="flex items-center">
        <StatusIndicatorComponent
          :asset="meteringPoint"
          no-tooltip
          class="mr-1.5"
        />
        <p>{{ $p(mpProps.status?.description_key ?? "status-unknown") }}</p>
      </div>
    </template>

    <!-- Type -->
    <p>Type</p>
    <p>{{ ASSET_TYPE_LABELS[mpProps.type] ?? mpProps.type }}</p>

    <!-- EMS provider -->
    <p>EMS Leverancier</p>
    <p>{{ mpProps.ems_provider }}</p>

    <!-- Forecast type -->
    <template v-if="mpProps.forecast_type">
      <p>Forecast</p>
      <p>{{ FORECAST_TYPE_LABELS[mpProps.forecast_type] }}</p>
    </template>

    <!-- DC power -->
    <template v-if="mpProps.type === ASSET_TYPE.SOLAR_PARK">
      <p>DC vermogen</p>
      <p>{{ formatKW(mpProps.dc_capacity_kwp, "Wp") }}</p>
    </template>

    <!-- AC Power -->
    <template
      v-if="![ASSET_TYPE.CONSUMER, ASSET_TYPE.BATTERY].includes(mpProps.type)"
    >
      <p>AC vermogen</p>
      <p>{{ formatKW(mpProps.max_power_out) }}</p>
    </template>

    <!-- Orientations -->
    <template v-if="mpProps.orientations && orientationRows.length > 0">
      <p class="col-span-2">
        Oriëntaties <BaseIcon name="corner-right-down" filled />
      </p>
      <BaseCard
        class="col-span-2 mt-1 overflow-hidden shadow-transparent"
        inner
      >
        <BaseTable :columns="orientationColumns" :rows="orientationRows">
          <template #azimuth="{ header }">
            {{ header }} <BaseTooltip description="Zuid is 180°" />
          </template>
        </BaseTable>
      </BaseCard>
    </template>

    <!-- Battery properties -->
    <template v-if="mpProps.type === ASSET_TYPE.BATTERY">
      <p>Capaciteit</p>
      <p>{{ formatKW(mpProps.energy_capacity, "Wh") }}</p>
      <p>Maximaal oplaadvermogen</p>
      <p>{{ formatKW(mpProps.max_power_in) }}</p>
      <p>Maximaal ontlaadvermogen</p>
      <p>{{ formatKW(mpProps.max_power_out) }}</p>
      <p>Minimale lading</p>
      <p>{{ formatPercentage(mpProps.min_soc) }}</p>
      <p>Maximale lading</p>
      <p>{{ formatPercentage(mpProps.max_soc) }}</p>
    </template>

    <!-- Wind turbine properties -->
    <template v-if="mpProps.wind_turbine_properties">
      <p class="col-span-2">
        Turbine eigenschappen
        <BaseIcon name="corner-right-down" filled />
      </p>
      <BaseCard
        class="col-span-2 mt-1 overflow-hidden shadow-transparent"
        inner
      >
        <BaseTable
          :columns="windTurbinePropertiesColumns"
          :rows="windTurbinePropertiesRows"
        />
      </BaseCard>
    </template>
  </div>
</template>

<script setup>
import { computed } from "vue"
import $p from "@/services/phraseService.js"
import { formatKW, formatPercentage } from "@/helpers/formatter.js"
import {
  ASSET_TYPE,
  ASSET_TYPE_LABELS,
  ASSET_TYPE_HIDDEN_STATUS,
} from "@/stores/repower/meteringPointStoreFactories.js"
import {
  BaseIcon,
  BaseTable,
  BaseCard,
  BaseTooltip,
} from "@repowerednl/ui-component-library"
import StatusIndicatorComponent from "@/components/information/StatusIndicatorComponent.vue"

const props = defineProps({
  meteringPoint: {
    type: Object,
    default: null,
  },
})

const FORECAST_TYPE_LABELS = {
  provider: "Ja",
  fallback: "Fallback",
}

const mpProps = computed(() => props.meteringPoint?.properties || {})

const orientationColumns = {
  percentage: { label: "Aandeel" },
  azimuth: { label: "Richting" },
  tilt: { label: "Hellingshoek" },
}

const orientationRows = computed(() => {
  const values = Object.values(mpProps.value.orientations || {})
  const hasKeys = (obj) =>
    "tilt" in obj && "azimuth" in obj && "percentage" in obj
  if (
    !Array.isArray(values) ||
    !values.every((obj) => {
      return obj && typeof obj === "object" && hasKeys(obj)
    })
  ) {
    return []
  }
  return values.map((orientation) => ({
    percentage: formatPercentage(orientation.percentage / 100),
    azimuth: `${orientation.azimuth}°`,
    tilt: `${orientation.tilt}°`,
  }))
})

const windTurbinePropertiesColumns = {
  name: { label: "Naam" },
  hub_height: { label: "Masthoogte" },
  rotor_diameter: { label: "Rotordiameter" },
}

const windTurbinePropertiesRows = computed(() =>
  Object.entries(mpProps.value.wind_turbine_properties || {}).map(
    ([key, value]) => ({
      name: key,
      hub_height: `${value.hub_height} m`,
      rotor_diameter: `${value.rotor_diameter} m`,
    }),
  ),
)
</script>
